import { ITEM_LOOKS, type ItemShape } from "@eldrun/content";
import { Euler, Mesh, Quaternion, Vector3, type ShaderMaterial } from "three";
import { AVATAR_SCALE } from "../assets/avatars.generated.ts";
import { itemGeometry } from "../render/item-meshes.ts";
import { itemModel } from "../render/item-models.ts";
import type { ModelLibrary } from "../render/model-library.ts";

/**
 * Onde o item é empunhado: na mão do boneco procedural (`rig.hand`) ou no encaixe da mão dos avatares
 * animados (`kaykit`, F49: o osso `handslotr`). A primeira pessoa (e o viewmodel preso à câmera) saiu na
 * F55: o item só aparece na mão do boneco e no chão.
 */
export type HeldView = "avatar" | "kaykit";

/** Pose do item: posição (m), giro (rad, XYZ) e escala, no espaço da mão. */
export interface HeldPose {
  readonly position: readonly [number, number, number];
  readonly rotation: readonly [number, number, number];
  readonly scale: number;
  /** Giro (rad) em torno do próprio cabo (+y), antes da pose. */
  readonly turn: number;
}

/**
 * O encaixe da mão dos avatares KayKit é o `rig.hand` do boneco procedural girado −90° em torno do cabo
 * (+y): o +x dele aponta para baixo com o braço solto, e o +z para fora. Os giros de cada forma valem
 * com esse acréscimo. Ele fica dentro do `ajuste` (escala ~0,82): o item compensa com 1/escala para ter
 * o tamanho do conteúdo.
 */
const KAYKIT_TURN = -Math.PI / 2;
const kaykitScale = (scale: number) => scale / AVATAR_SCALE;

/**
 * Formas que os avatares KayKit seguram na mão esquerda (`handslotl`), com o giro em torno do cabo: o
 * clipe `arco` puxa a corda com a direita e estende a esquerda com o arco. Puxado, o +y do encaixe
 * esquerdo aponta para cima (o arco em pé) e o −π/2 leva a corda (−x do modelo) para quem atira.
 */
const KAYKIT_LEFT: Partial<Record<ItemShape, number>> = { arco: -Math.PI / 2 };

/** Em que mão do avatar animado o item vai: a esquerda para o arco, a direita para o resto. */
export function kaykitHand(itemId: string): "direita" | "esquerda" {
  const shape = ITEM_LOOKS[itemId]?.shape;
  return shape !== undefined && KAYKIT_LEFT[shape] !== undefined ? "esquerda" : "direita";
}

/** Malhas procedurais (sem modelo): as poses de antes da F47, com o cabo no meio da malha. */
const PROCEDURAL: Readonly<Record<HeldView, HeldPose>> = {
  avatar: { position: [0, 0, 0], rotation: [0, 0, 0], scale: 1.1, turn: 0 },
  kaykit: { position: [0, 0, 0], rotation: [0, 0, 0], scale: kaykitScale(1.1), turn: KAYKIT_TURN },
};

/** A tora e o lingote do pack são compridos em z: de lado (em x) na mão, como a tora procedural. */
const SIDEWAYS: Partial<Record<ItemShape, number>> = { tora: Math.PI / 2, lingote: Math.PI / 2 };

/**
 * Giro dos modelos na mão do boneco, em torno do cabo: o `rig.hand` aponta o cabo (+y) para a frente e
 * o +z para o ombro. As cabeças do pack ficam para −x (para dentro); viradas para −z, a lâmina desce com
 * o braço solto e vai à frente no golpe. A corda do arco (−x) vai para +z: para quem atira, com o braço
 * erguido.
 */
const AVATAR_TURN: Partial<Record<ItemShape, number>> = {
  machado: -Math.PI / 2,
  picareta: -Math.PI / 2,
  marreta: -Math.PI / 2,
  porrete: -Math.PI / 2,
  lanca: -Math.PI / 2,
  adaga: -Math.PI / 2,
  espada: -Math.PI / 2,
  arco: Math.PI / 2,
  ...SIDEWAYS,
};

/** Pose do modelo da forma na mão do boneco, no tamanho do conteúdo (`ITEM_SHAPES`). */
export function modelPose(shape: ItemShape, view: HeldView): HeldPose {
  if (view === "avatar") return { position: [0, 0, 0], rotation: [0, 0, 0], scale: 1, turn: AVATAR_TURN[shape] ?? 0 };
  return { position: [0, 0, 0], rotation: [0, 0, 0], scale: kaykitScale(1), turn: KAYKIT_LEFT[shape] ?? (AVATAR_TURN[shape] ?? 0) + KAYKIT_TURN };
}

const euler = new Euler();
const turn = new Quaternion();
const up = new Vector3(0, 1, 0);

/** Aplica a pose guardada em `userData.pose` (a mão do boneco o leva no golpe). */
export function poseHeld(mesh: Mesh): void {
  const pose = mesh.userData["pose"] as HeldPose;
  mesh.position.set(pose.position[0], pose.position[1], pose.position[2]);
  mesh.quaternion.setFromEuler(euler.set(pose.rotation[0], pose.rotation[1], pose.rotation[2])).multiply(turn.setFromAxisAngle(up, pose.turn));
  mesh.scale.setScalar(pose.scale);
}

/**
 * Malha do item empunhado, já na pose (`userData.pose`): o modelo do item (LOD 0, cor do tier) ou a
 * malha procedural de reserva, cada um com a sua pose. O material é o de folhagem sem o sumiço perto da
 * câmera: o item vai junto do boneco, que some por conta própria quando a câmera encosta nele (F55);
 * com o sumiço, ele pontilharia antes do corpo. Lança erro (erro de programação, que os e2e pegam no
 * console).
 */
export function heldMesh(itemId: string, library: ModelLibrary, material: ShaderMaterial, view: HeldView): Mesh | undefined {
  if (material.defines?.["USE_NEAR_FADE"] !== undefined) throw new Error("item na mão: material com sumiço perto da câmera");
  if (!itemId) return undefined;
  const model = itemModel(itemId, 0, library);
  const shape = ITEM_LOOKS[itemId]?.shape;
  const mesh = new Mesh(model ?? itemGeometry(itemId), material);
  mesh.name = "item-na-mao";
  mesh.userData["pose"] = model && shape ? modelPose(shape, view) : PROCEDURAL[view];
  mesh.userData["model"] = model !== undefined;
  poseHeld(mesh);
  return mesh;
}
